import { useState } from "react";
import {
  useUpdateFormAssignmentMutation,
  useCreateFormAssignmentMutation,
  useCreateDottLinkNotificationMutation,
} from "@services/formAssignments";
import { useAppDispatch } from "@hooks/useTypedRedux";
import { showNotification } from "@features/modals/notificationModalSlice";
import useAuth from "@features/auth/useAuth";
import { PendingFormDataToAssignPerPatient } from "@features/formAssignments/patientInfoAndFormsPage/forms/usePendingFormDataToAssign";
import {
  IFormAssignmentForm,
  IInOfficeEvent,
} from "../types/formAssignments/IInOfficeEvent";
import { useGetFeatureFlag } from "@hooks/useGetFeatureFlag";
import { EFeatureFlags } from "../enums/EFeatureFlags";

export default function useAssignFormsAndSendToDottlink() {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { isEnabled: isFamilyEnabled } = useGetFeatureFlag(
    EFeatureFlags.FastrAppFamilyEnabled
  );

  const [updateFormAssignment] = useUpdateFormAssignmentMutation();
  const [createFormAssignment] = useCreateFormAssignmentMutation();
  const [createDottLinkNotification] = useCreateDottLinkNotificationMutation();
  const [logout] = useAuth().logout;

  const determinePackageName = (
    forms: IFormAssignmentForm[],
    existingFormAssignment?: IInOfficeEvent["formAssignment"]
  ): string => {
    // In Leap, a custom package name can be set. We don't wnat to overwrite that.
    if (existingFormAssignment?.packageName) {
      return existingFormAssignment.packageName;
    }

    // If all forms belong to the same package, then we should use that
    // package name.
    const packageNames = forms.map((form) => form.packageName);
    if (new Set(packageNames).size === 1 && packageNames[0]) {
      return packageNames[0];
    }

    return "Custom Package";
  };

  const assignFormsAndSendToDottlink = async (
    pendingFormDataToAssign: PendingFormDataToAssignPerPatient
  ): Promise<void> => {
    setIsLoading(true);

    const atLeastOnePatientHasForms = Object.values(
      pendingFormDataToAssign
    ).some((dataToAssign) => dataToAssign.incompleteForms.length !== 0);
    if (!atLeastOnePatientHasForms) {
      throw new Error("No forms to assign");
    }

    try {
      const formAssignmentPromises = Object.values(pendingFormDataToAssign).map(
        async ({
          incompleteForms,
          inOfficeEvent: { patient, appointment, formAssignment },
        }) => {
          const noFormAssignmentNeeded =
            formAssignment == null && incompleteForms.length === 0;
          if (isFamilyEnabled && noFormAssignmentNeeded) {
            return;
          }

          const formAssignmentHasNotChanged =
            formAssignment &&
            JSON.stringify(formAssignment.forms.map((form) => form.id)) ===
              JSON.stringify(incompleteForms.map((form) => form.id));
          if (isFamilyEnabled && formAssignmentHasNotChanged) {
            return {
              formAssignmentId: formAssignment.id,
              patientId: patient.id,
            };
          }

          const formsToSave = incompleteForms.map((form, index) => ({
            formId: form.id,
            formName: form.name,
            order: index + 1,
            formStatus: form.status,
            responseId: form.responseId,
            formAssignmentId: form.formAssignmentId,
            packageId: form.packageId,
            hasOfficeDetailsQuestion: form.hasOfficeDetailsQuestion,
            hasPatientInfoQuestions: form.hasPatientInfoQuestions,
            requiresProviderSignature: form.requiresProviderSignature,
          }));

          const packageName = determinePackageName(
            incompleteForms,
            formAssignment
          );

          let formAssignmentId: string;

          const patientId = patient.id;

          if (formAssignment) {
            await updateFormAssignment({
              patientId,
              formAssignmentId: formAssignment.id,
              packageName,
              forms: formsToSave,
              appointmentId: appointment?.id,
            }).unwrap();
            formAssignmentId = formAssignment.id;
          } else {
            const createResponse = await createFormAssignment({
              patientId,
              forms: formsToSave,
              packageName,
              appointmentId: appointment?.id,
            }).unwrap();
            formAssignmentId = createResponse.id;
          }

          return {
            formAssignmentId: formAssignmentId,
            patientId,
          };
        }
      );

      const formAssignments = (
        await Promise.all(formAssignmentPromises)
      ).filter((formAssignment) => formAssignment != null);

      if (formAssignments.length === 0) {
        throw new Error("Form assignment returned no results");
      }

      const formAssignmentsWithForms = formAssignments.filter(
        (formAssignment) =>
          pendingFormDataToAssign[formAssignment.patientId]?.incompleteForms
            .length > 0
      );

      // Ideally this API would accept all the formAssignments and return a single
      // dottlink URL, but currently that does not work. Handling the family
      // assignment through query params fully on the front end instead.
      const formAssignmentsToSend = [formAssignmentsWithForms[0]];

      const dottlinkResponse = await createDottLinkNotification(
        formAssignmentsToSend
      ).unwrap();

      const dottLink = dottlinkResponse[0].dottLink;

      if (dottLink == null || dottlinkResponse[0].dottLink === "") {
        throw new Error("Empty dottlink URL");
      }

      await logout();

      const dottLinkWithFamilyMembers = `${dottLink}&familyMembers=${JSON.stringify(formAssignmentsWithForms)}`;

      // "replace" is intentionally used so the /form-assignemnts
      // route is not shown in the back/forward history.
      window.location.replace(dottLinkWithFamilyMembers);
    } catch (error) {
      console.error("Error in assignFormsAndSendToDottlink:", error);
      dispatch(
        showNotification({
          message: "Failed to save forms or send notification",
          severity: "error",
        })
      );
      setIsLoading(false);
    }
  };

  return {
    assignFormsAndSendToDottlink,
    isLoading,
  };
}
